import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getInitials } from './UserContextPanel';

function HeaderUserMenu({ className = 'hidden sm:block' }) {
  const { user, account, memberships, switchAccount, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) {
    return null;
  }

  const handleWorkspaceSelect = async (accountId) => {
    setIsOpen(false);
    if (accountId === account?.id) {
      return;
    }
    await switchAccount(accountId);
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const hasMultipleWorkspaces = memberships.length > 1;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-content-primary hover:bg-slate-50 transition-colors"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Menu do usuário"
      >
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-forge-100 text-forge-700 text-xs font-semibold"
          aria-hidden
        >
          {getInitials(user.name)}
        </span>
        <span className="hidden md:inline max-w-[120px] truncate">{user.name}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-content-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-lg border border-slate-200 bg-white shadow-lg z-50 py-1"
          role="menu"
        >
          <div className="px-3 py-2 border-b border-surface-border">
            <div className="text-sm font-medium text-content-primary truncate">{user.name}</div>
            <div className="text-xs text-content-muted truncate">{user.email}</div>
          </div>

          {account && (
            <div className="px-3 py-2 border-b border-surface-border">
              <div className="text-[11px] font-medium uppercase tracking-wide text-content-muted mb-1.5">
                Workspace
              </div>
              {hasMultipleWorkspaces ? (
                <div className="space-y-0.5">
                  {memberships.map((membership) => {
                    const isActive = membership.account_id === account.id;
                    const label = membership.account?.name || membership.account_id;
                    return (
                      <button
                        key={membership.account_id}
                        type="button"
                        role="menuitem"
                        onClick={() => handleWorkspaceSelect(membership.account_id)}
                        className={`w-full text-left rounded-md px-2 py-1.5 text-sm transition-colors ${
                          isActive
                            ? 'bg-forge-50 text-forge-700 font-medium'
                            : 'text-content-primary hover:bg-slate-50'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-content-secondary truncate">{account.name}</div>
              )}
            </div>
          )}

          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-content-muted hover:bg-slate-50 hover:text-content-primary transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden />
            Sair
          </button>
        </div>
      )}
    </div>
  );
}

export default HeaderUserMenu;
